import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card } from '../ui/card';
import { Switch } from '../ui/switch';
import { Trash2, Mail, Clock, Plus } from 'lucide-react';
import {
  listReportSchedules, createReportSchedule, toggleReportSchedule, deleteReportSchedule
} from '../../services/api';

/**
 * Schedules Manager — lets an Admin/user pick a saved report filter and
 * schedule it as a recurring email (daily or weekly at HH:MM UTC).
 */
export const SchedulesManager = ({ savedFilters = [] }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', filterId: '', frequency: 'daily', time: '09:00', weekday: 1, recipients: '', format: 'excel' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try { setLoading(true); const r = await listReportSchedules(); setSchedules(r.data || []); }
    catch { toast.error('Failed to load schedules'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.filterId) return toast.error('Pick a saved filter');
    const emails = form.recipients.split(',').map(e => e.trim()).filter(e => e.includes('@'));
    if (!emails.length) return toast.error('At least one recipient email is required');
    setCreating(true);
    try {
      await createReportSchedule({ ...form, recipients: emails });
      setForm({ name: '', filterId: '', frequency: 'daily', time: '09:00', weekday: 1, recipients: '', format: 'excel' });
      await load();
      toast.success('Schedule created');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create schedule');
    } finally { setCreating(false); }
  };

  const handleToggle = async (s) => {
    try { await toggleReportSchedule(s.id, !s.enabled); await load(); }
    catch { toast.error('Failed to toggle'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    try { await deleteReportSchedule(id); await load(); toast.success('Deleted'); }
    catch { toast.error('Failed to delete'); }
  };

  const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="space-y-4" data-testid="schedules-manager">
      <Card className="p-4">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Plus className="w-4 h-4" />New Schedule</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input data-testid="schedule-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Weekly Freezed Review" />
          </div>
          <div>
            <Label className="text-xs">Saved Filter</Label>
            <Select value={form.filterId || 'none'} onValueChange={(v) => setForm({ ...form, filterId: v === 'none' ? '' : v })}>
              <SelectTrigger data-testid="schedule-filter"><SelectValue placeholder="Pick a preset" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none" disabled>Pick a preset</SelectItem>
                {savedFilters.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Frequency</Label>
            <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
              <SelectTrigger data-testid="schedule-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Time (UTC)</Label>
            <Input data-testid="schedule-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
          </div>
          {form.frequency === 'weekly' && (
            <div>
              <Label className="text-xs">Day</Label>
              <Select value={String(form.weekday)} onValueChange={(v) => setForm({ ...form, weekday: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="md:col-span-2">
            <Label className="text-xs">Recipients (comma-separated)</Label>
            <Input data-testid="schedule-recipients" value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })} placeholder="ops@company.com, cfo@company.com" />
          </div>
          <div className="flex items-end">
            <Button data-testid="schedule-create-btn" onClick={handleCreate} disabled={creating} className="w-full">{creating ? 'Creating…' : 'Create'}</Button>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Mail className="w-4 h-4" />Active Schedules ({schedules.length})</h3>
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : schedules.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No schedules yet. Create one above to email a saved filter on a cron.</p>
        ) : (
          <div className="space-y-2">
            {schedules.map(s => (
              <div key={s.id} className="flex items-center justify-between p-3 border rounded-lg bg-slate-50" data-testid={`schedule-row-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate">{s.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{s.frequency === 'weekly' ? WEEKDAYS[s.weekday] + ' ' : ''}{s.time} UTC</span>
                    <span>·</span>
                    <span className="truncate">{s.recipients.join(', ')}</span>
                    {s.lastRunAt && <><span>·</span><span>Last: {new Date(s.lastRunAt).toLocaleString()}</span></>}
                    {s.lastStatus && <span className={s.lastStatus === 'OK' ? 'text-green-700' : 'text-red-700'}>({s.lastStatus})</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3 ml-3">
                  <Switch checked={s.enabled} onCheckedChange={() => handleToggle(s)} data-testid={`schedule-toggle-${s.id}`} />
                  <button onClick={() => handleDelete(s.id)} className="text-red-600 hover:bg-red-50 p-1.5 rounded" data-testid={`schedule-delete-${s.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default SchedulesManager;
